import {PlayerId} from '@glark-newco/game-library';
import dayjs, {Dayjs} from 'dayjs';
import {useMemo} from 'react';
import {Objective, useObjectivesMap} from '../state/PlayerObjectivesSlice';

export const useObjectivesStats = () => {
    const objectivesMap = useObjectivesMap();
    return useMemo(() => {
        const allObjectivesCount = Object.values(objectivesMap).flatMap((o: Objective[]) => o).length;
        const completeObjectivesCount = Object.values(objectivesMap)
            .flatMap((o: Objective[]) => o).filter(o => !!o.ringId).length;
        return {
            allObjectivesCount, completeObjectivesCount, isComplete: allObjectivesCount === completeObjectivesCount,
        };
    }, [objectivesMap]);
};

export const usePlayerStats = () => {
    const objectivesMap = useObjectivesMap();
    return useMemo(() => {
        const allPlayersCount = Object.keys(objectivesMap).length;
        const completePlayersCount = Object.values(objectivesMap)
            .filter((playerObjectives: Objective[]) => playerObjectives.every(o => !!o.ringId)).length;
        return {
            allPlayersCount, completePlayersCount, isComplete: allPlayersCount === completePlayersCount,
        };
    }, [objectivesMap]);
};

export const useAllObjectivesComplete = () => {
    const objectivesMap = useObjectivesMap();
    return useMemo(() => {
        // All players, every objective has a ring.
        return Object.values(objectivesMap).flatMap((o: Objective[]) => o).every(o => !!o.ringId);
    }, [objectivesMap]);
};


// #########################################
// #   useFirstPlayerComplete
// #########################################

function playerTimestamp(a: PlayerCompletionTime, b: PlayerCompletionTime) {
    return a.completionTime!.valueOf() - b.completionTime!.valueOf();
}

interface PlayerCompletionTime {
    playerId: PlayerId;
    completionTime: Dayjs | undefined;
}

export const useFirstPlayerComplete = (): PlayerId | undefined => {
    const objectivesMap = useObjectivesMap();

    const completePlayers: PlayerCompletionTime[] = useMemo(() => {
        return Object.entries(objectivesMap)
            .filter(([, objs]) => objs.every(o => !!o.ringId))
            .map(([playerId, objs]) => {
                const maxCompletionTime = objs.reduce(
                    (max: Dayjs, o: Objective) =>  (!o.completionTime || max.isAfter(o.completionTime)) ? max : o.completionTime,
                    dayjs(0),
                );
                return ({
                    playerId,
                    completionTime: maxCompletionTime,
                });
            });
    }, [objectivesMap]);

    // Return the player with the earliest *overall* completion timestamp,
    // or undefined if no player has completed all objectives.
    return useMemo(() => {
        if (completePlayers.length === 0) return undefined;
        return completePlayers.sort(playerTimestamp)[0].playerId;
    }, [completePlayers]);
};
